import { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';

const useAsyncAction = (action, dependencies = []) => {
    const dispatch = useDispatch();
    const unmounted = useRef(false);
    const [isLoading, setIsLoading] = useState(false);
    const [isError, setIsError] = useState(false);
    const [isSuccessful, setIsSuccessful] = useState(false);
    const [error, setError] = useState(false);

    const asyncAction = useCallback(
        (...args) => {
            async function callback() {
                setIsLoading(true);

                try {
                    const res = await dispatch(action(...args));
                    if (!unmounted.current && !res.error) {
                        setIsError(false);
                        setIsLoading(false);
                        setIsSuccessful(true);
                        setError(false);
                    }

                    return res;
                } catch (e) {
                    if (!unmounted.current) {
                        setIsLoading(false);
                        setIsSuccessful(false);
                        setIsError(true);
                        setError(e);
                    }

                    return e;
                }
            }

            callback();
        },
        [action]
    );

    useEffect(() => {
        if (!unmounted.current) {
            setIsLoading(false);
            setIsSuccessful(false);
            setIsError(false);
        }
    }, [...dependencies]);

    useEffect(() => {
        unmounted.current = false;

        return () => {
            unmounted.current = true;
        };
    }, []);

    return { asyncAction, isLoading, isSuccessful, isError, setIsLoading, setIsError, setIsSuccessful, error };
};

export default useAsyncAction;
